<template>
  <div class="ship-trajectory-container">
    <div class="filter-section">
      <el-form :inline="true" :model="queryParams" class="search-form">
        <el-form-item label="船舶查询">
          <el-input
            v-model="queryParams.keyword"
            placeholder="请输入船舶名称或MMSI号"
            clearable
            style="width: 220px"
          ></el-input>
        </el-form-item>
        <el-form-item label="时间范围">
          <el-date-picker
            v-model="queryParams.timeRange"
            type="datetimerange"
            range-separator="至"
            start-placeholder="开始时间"
            end-placeholder="结束时间"
            value-format="yyyy-MM-dd HH:mm:ss"
            :default-time="['00:00:00', '23:59:59']"
            :picker-options="pickerOptions"
          ></el-date-picker>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="searchTrajectory" :loading="loading">查询</el-button>
          <el-button @click="resetQuery">重置</el-button>
          <el-button type="success" @click="exportTrajectoryData" :disabled="!trajectoryData.length">导出</el-button>
        </el-form-item>
      </el-form>
    </div>

    <div class="main-content">
      <el-card class="trajectory-info">
        <el-table
          v-loading="loading"
          :data="trajectoryDisplayData"
          stripe
          style="width: 100%; margin-top: 15px;"
          max-height="500"
          :header-cell-style="{ backgroundColor: '#f5f7fa' }"
        >
         <el-table-column prop="mmsi" label="MMSI" width="200"></el-table-column>
          <el-table-column prop="vesselName" label="船舶名称" width="200">
            <template slot-scope="scope">
              {{ scope.row.vesselName || '<无>' }}
            </template>
          </el-table-column>
          <el-table-column prop="vesselType" label="船舶类型" width="90">
            <template slot-scope="scope">
              {{ vesselTypeDict[scope.row.vesselType] || scope.row.vesselType || '-' }}
            </template>
          </el-table-column>
          <el-table-column prop="timestamp" label="时间" width="180">
            <template slot-scope="scope">
              {{ formatTime(scope.row.timestamp) }}
            </template>
          </el-table-column>
          <el-table-column prop="longitude" label="经度" width="180">
            <template slot-scope="scope">
              {{ formatCoordinate(scope.row.longitude, 'lng') }}
            </template>
          </el-table-column>
          <el-table-column prop="latitude" label="纬度" width="180">
            <template slot-scope="scope">
              {{ formatCoordinate(scope.row.latitude, 'lat') }}
            </template>
          </el-table-column>
          <el-table-column prop="speed" label="速度(节)" width="100">
            <template slot-scope="scope">
              {{ scope.row.speed === null ? '无' : scope.row.speed }}
            </template>
          </el-table-column>
          <el-table-column prop="heading" label="航向" width="100">
            <template slot-scope="scope">
              {{ formatHeading(scope.row.heading) }}
            </template>
          </el-table-column>
          <el-table-column label="状态" width="100">
            <template slot-scope="scope">
              <el-tag 
                :type="scope.row.isWarning ? 'danger' : 'success'" 
                size="mini"
              >
                {{ scope.row.isWarning ? '预警' : '正常' }}
              </el-tag>
            </template>
          </el-table-column>
        </el-table>
        
        <div class="pagination-container">
          <el-pagination
            v-if="totalCount > 0"
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
            :current-page="queryParams.pageNum"
            :page-sizes="[10, 20, 50, 100]"
            :page-size="queryParams.pageSize"
            layout="total, sizes, prev, pager, next, jumper"
            :total="totalCount">
          </el-pagination>
        </div>
        
        <div v-if="!trajectoryData.length && !loading" class="empty-data">
          <i class="el-icon-map-location"></i>
          <p>请输入查询条件查询船舶航迹数据</p>
        </div>
      </el-card>
    </div>
  </div>
</template>

<script>
import { searchVesselTrajectory } from '@/api/vessel-trajectory'
import { getAllVesselTypes } from '@/api/vessel'
import { formatDate } from '@/utils/mapUtils'
import { convertToDMS } from '@/utils/coordinateUtils'

export default {
  name: 'ShipTrajectory',
  data() {
    return {
      trajectoryData: [],
      trajectoryDisplayData: [],
      loading: false,
      totalCount: 0,
      totalPages: 0,
      avgSpeed: '0.00',
      maxSpeed: '0.00',
      vesselCount: 0,
      vesselTypeDict: {}, // 船舶类型字典
      queryParams: {
        keyword: '',
        timeRange: [
          formatDate(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd HH:mm:ss'),
          formatDate(new Date(), 'yyyy-MM-dd HH:mm:ss')
        ],
        pageNum: 1,
        pageSize: 10
      },
      pickerOptions: {
        shortcuts: [
          {
            text: '最近一周',
            onClick(picker) {
              const end = new Date()
              const start = new Date()
              start.setTime(start.getTime() - 7 * 24 * 60 * 60 * 1000)
              picker.$emit('pick', [start, end])
            }
          },
          {
            text: '最近一个月',
            onClick(picker) {
              const end = new Date()
              const start = new Date()
              start.setTime(start.getTime() - 30 * 24 * 60 * 60 * 1000)
              picker.$emit('pick', [start, end])
            }
          },
          {
            text: '最近三个月',
            onClick(picker) {
              const end = new Date()
              const start = new Date()
              start.setTime(start.getTime() - 90 * 24 * 60 * 60 * 1000)
              picker.$emit('pick', [start, end])
            }
          }
        ],
        disabledDate(time) {
          const threeMonthsAgo = Date.now() - 90 * 24 * 60 * 60 * 1000
          const tooLateTime = Date.now()
          return time.getTime() < threeMonthsAgo || time.getTime() > tooLateTime
        }
      }
    }
  },
  mounted() {
    this.searchTrajectory();
    this.fetchVesselTypes(); // 获取船舶类型字典
  },
  methods: {
    async searchTrajectory() {
      if (!this.queryParams.timeRange || this.queryParams.timeRange.length !== 2) {
        this.$message.warning('请选择时间范围')
        return
      }
      
      const startTime = new Date(this.queryParams.timeRange[0]).getTime()
      const endTime = new Date(this.queryParams.timeRange[1]).getTime()
      const threeMonths = 90 * 24 * 60 * 60 * 1000
      
      if (endTime - startTime > threeMonths) {
        this.$message.warning('查询时间范围不能超过3个月')
        return
      }
      
      this.loading = true
      
      try {
        const params = {
          keyword: this.queryParams.keyword,
          startTime: this.queryParams.timeRange[0],
          endTime: this.queryParams.timeRange[1],
          pageNum: this.queryParams.pageNum,
          pageSize: this.queryParams.pageSize
        }
        
        const res = await searchVesselTrajectory(params)
        console.log('API返回数据:', res)
        
        if (res.code === 200 && res.data) {
          this.trajectoryData = res.data.list || []
          this.trajectoryDisplayData = res.data.list || []
          this.totalCount = res.data.total || 0
          this.totalPages = res.data.pages || 0
          
          if (this.trajectoryData.length > 0) {
            this.calculateStatistics()
          } else {
            this.avgSpeed = '0.00'
            this.maxSpeed = '0.00'
            this.vesselCount = 0
            this.$message.info('未查询到航迹数据')
          }
        } else {
          this.trajectoryData = []
          this.trajectoryDisplayData = []
          this.totalCount = 0
          this.totalPages = 0
          this.$message.warning('查询航迹数据失败')
        }
      } catch (error) {
        console.error('查询航迹失败:', error)
        this.$message.error('查询航迹失败，请检查网络连接')
        this.trajectoryData = []
        this.trajectoryDisplayData = []
      } finally {
        this.loading = false
      }
    },
    
    async fetchVesselTypes() {
      try {
        const res = await getAllVesselTypes()
        if (res.code === 200 && res.data) {
          // 将数据转换为以typeCode为键，typeName为值的字典对象
          const dict = {}
          res.data.forEach(item => {
            dict[item.typeCode] = item.typeName
          })
          this.vesselTypeDict = dict
        } else {
          this.$message.warning('获取船舶类型失败')
        }
      } catch (error) {
        console.error('获取船舶类型失败:', error)
        this.$message.error('获取船舶类型失败，请检查网络连接')
      }
    },
    
    resetQuery() {
      this.queryParams = {
        keyword: '',
        timeRange: [
          formatDate(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd HH:mm:ss'),
          formatDate(new Date(), 'yyyy-MM-dd HH:mm:ss')
        ],
        pageNum: 1,
        pageSize: 10
      }
      this.trajectoryData = []
      this.trajectoryDisplayData = []
      this.totalCount = 0
      this.totalPages = 0
      this.avgSpeed = '0.00'
      this.maxSpeed = '0.00'
      this.vesselCount = 0
    },
    
    calculateStatistics() {
      if (!this.trajectoryData.length) return
      
      let totalSpeed = 0
      let maxSpeed = 0
      let validSpeedCount = 0
      
      this.trajectoryData.forEach(point => {
        if (point.speed !== null && point.speed !== undefined) {
          totalSpeed += point.speed
          maxSpeed = Math.max(maxSpeed, point.speed)
          validSpeedCount++
        }
      })
      
      this.avgSpeed = validSpeedCount > 0 ? (totalSpeed / validSpeedCount).toFixed(2) : '0.00'
      this.maxSpeed = maxSpeed.toFixed(2)
      
      const vesselSet = new Set()
      this.trajectoryData.forEach(point => {
        if (point.mmsi) {
          vesselSet.add(point.mmsi)
        }
      })
      this.vesselCount = vesselSet.size
    },
    
    handleCurrentChange(page) {
      this.queryParams.pageNum = page
      this.searchTrajectory()
    },
    
    handleSizeChange(size) {
      this.queryParams.pageSize = size
      this.queryParams.pageNum = 1
      this.searchTrajectory()
    },
    
    exportTrajectoryData() {
      if (!this.trajectoryData.length) {
        this.$message.warning('没有可导出的数据')
        return
      }
      
      let csvContent = "data:text/csv;charset=utf-8,"
      csvContent += "船舶名称,MMSI,船舶类型,时间,经度,纬度,速度(节),航向,状态\n"
      
      this.trajectoryData.forEach(point => {
        const vesselName = point.vesselName || '<无>'
        const vesselType = this.vesselTypeDict[point.vesselType] || point.vesselType || '<无>'
        const status = point.isWarning ? '预警' : '正常'
        const heading = this.formatHeading(point.heading)
        
        const row = [
          vesselName,
          point.mmsi || '',
          vesselType,
          this.formatTime(point.timestamp),
          this.formatCoordinate(point.longitude, 'lng'),
          this.formatCoordinate(point.latitude, 'lat'),
          point.speed || 0,
          heading,
          status
        ].join(',')
        csvContent += row + "\n"
      })
      
      const encodedUri = encodeURI(csvContent)
      const link = document.createElement("a")
      link.setAttribute("href", encodedUri)
      link.setAttribute("download", `船舶航迹数据_${formatDate(new Date(), 'yyyyMMddHHmmss')}.csv`)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
    
    formatTime(timestamp) {
      if (!timestamp) return ''
      const date = new Date(timestamp)
      return formatDate(date, 'yyyy-MM-dd HH:mm:ss')
    },
    
    // 格式化坐标显示，最多显示6位小数
    formatCoordinate(value, type) {
      if (value === null || value === undefined || value >= 360) return '无'
      return convertToDMS(value, type)
    },
    
    // 格式化航向，大于等于360显示为"-/-"
    formatHeading(heading) {
      if (heading === null || heading === undefined || heading >= 360) return '无'
      return heading
    }
  }
}
</script>

<style scoped>
.ship-trajectory-container {
  height: 100%;
  padding: 15px;
  background-color: #f5f7fa;
  display: flex;
  flex-direction: column;
  overflow: hidden; /* 防止容器产生滚动条 */
}

.filter-section {
  background-color: #fff;
  border-radius: 4px;
  padding: 15px 15px 0 15px;
  margin-bottom: 15px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.1);
}

.search-form {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
}

.main-content {
  flex: 1;
  min-height: calc(100vh - 240px);
  overflow: hidden; /* 防止内容部分产生滚动条 */
}

.trajectory-info {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.trajectory-info >>> .el-card__body {
  flex: 1;
  overflow: auto; /* 仅在卡片内部内容区域允许滚动 */
  padding-bottom: 10px; /* 为分页留出空间 */
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.trajectory-summary {
  margin-bottom: 15px;
}

.summary-title {
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 10px;
  color: #303133;
}

.pagination-container {
  margin-top: 15px;
  display: flex;
  justify-content: flex-end;
}

.empty-data {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 300px;
  color: #909399;
}

.empty-data i {
  font-size: 64px;
  margin-bottom: 15px;
  color: #dcdfe6;
}
</style> 